import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { _className, LL_H_C, LL_V_L, openContent, ProtoComponent, stopPropagation } from '@nu-art/thunderstorm-frontend/index';
import { _keys, BadImplementationException, dbObjectToId, Filter, MUSTNeverHappenException, sortArray } from '@nu-art/ts-common';
import '../Panel_MultiVarList/Panel_MultiVarList.scss';
import { ModuleFE_Tag, ModuleFE_Variable, Panel_Header, Tooltip_VariableInformation } from '@app/hcs-frontend';
import { DBDef_DiseaseProfile, DBDef_DiseaseView, DBDef_MultiVar, noConnectedViewDPView } from '@app/dp-shared';
import { ICONS } from '@app/styles';
import { ModuleFE_DiseaseProfile, ModuleFE_DiseaseValue, ModuleFE_DiseaseView, ModuleFE_MultiVar } from '../../../_entity.js';
import { Dialog_MultiVar_Editor_V2 } from '../../dialogs/Dialog_MultiVar_Editor/Dialog_MultiVar_Editor_V2.js';
import { defaultTooltipDelay, variableType_AttributeInstance } from '@app/hcs-shared';
export class Panel_MultiVarValList extends ProtoComponent {
    //######################### Static #########################
    static defaultProps = {
        keys: ['selected', 'filterMultiVar'],
    };
    //######################### Life Cycle #########################
    __onVariableUpdated() {
        //Re-render to show updated names of multivars
        this.forceUpdate();
    }
    __onDiseaseProfileUpdated(...params) {
        //TODO: i'm not sure why we care about an updated in DP
        this.reDeriveState();
    }
    __onMultiVarUpdated(...params) {
        //Derive to get updated list of multi vars
        if (params[0] === 'delete' || params[0] === 'delete-multi') {
            if (this.state.selectedMultiVarId === params[1]._id) {
                const selected = { ...this.getQueryParam('selected', {}) };
                delete selected[DBDef_MultiVar.dbKey];
                this.setQueryParam('selected', selected);
            }
        }
        this.reDeriveState();
    }
    __onDiseaseValueUpdated(...params) {
        //Re-render to show updated counts
        this.forceUpdate();
    }
    deriveStateFromProps(nextProps, state) {
        const selected = { ...this.getQueryParam('selected', {}) };
        state.filter = this.getQueryParam('filterMultiVar');
        state.multiVars = [];
        state.sortMethod ??= 'abc';
        state.selectedMultiVarId = selected[DBDef_MultiVar.dbKey];
        state.diseaseProfile = ModuleFE_DiseaseProfile.cache.unique(selected[DBDef_DiseaseProfile.dbKey]);
        state.diseaseView = ModuleFE_DiseaseView.cache.unique(selected[DBDef_DiseaseView.dbKey]);
        //If missing a selected DP or DV, return here
        if (!state.diseaseProfile || !state.diseaseView)
            return state;
        //Get Related Multivars
        state.multiVars = ModuleFE_MultiVar.cache.filter(mv => {
            if (mv.diseaseProfileId !== state.diseaseProfile._id)
                return false;
            if (state.diseaseView._id === noConnectedViewDPView._id)
                return ModuleFE_DiseaseView.checkVariableMatchesNoViews(mv);
            return ModuleFE_DiseaseView.filterMultiVarByEnforcedTag(state.diseaseView, mv);
        });
        //Filter multivars
        if (state.filter && state.filter.length >= 3) {
            const filter = new Filter(mv => [this.resolveDisplayText(mv)]);
            state.multiVars = filter.filter(state.multiVars, state.filter);
        }
        //Fix selected MV
        const firstMVId = this.getSortedMultiVars(state)[0]?._id;
        //If no MVs to show
        if (!firstMVId) {
            //But there is a selected MV
            if (state.selectedMultiVarId) {
                delete selected[DBDef_MultiVar.dbKey];
                this.setQueryParam('selected', selected);
            }
            return state;
        }
        //If no MV is selected
        if (!state.selectedMultiVarId) {
            state.selectedMultiVarId = firstMVId;
            selected[DBDef_MultiVar.dbKey] = firstMVId;
            this.setQueryParam('selected', selected);
            return state;
        }
        //If the selected MV does not match the DP or DV
        const selectedMV = ModuleFE_MultiVar.cache.unique(state.selectedMultiVarId);
        if (!selectedMV)
            throw new MUSTNeverHappenException(`Could not get MV for id ${state.selectedMultiVarId}`);
        if (selectedMV.diseaseProfileId !== state.diseaseProfile._id || !ModuleFE_DiseaseView.filterMultiVarByEnforcedTag(state.diseaseView, selectedMV)) {
            state.selectedMultiVarId = firstMVId;
            selected[DBDef_MultiVar.dbKey] = firstMVId;
            this.setQueryParam('selected', selected);
        }
        return state;
    }
    //######################### Logic #########################
    resolveDisplayText(mv) {
        const variables = mv.variables.map(mvv => ModuleFE_Variable.cache.unique(mvv.variableId));
        const firstVar = variables[0];
        const attributeInstance = variables.find(variable => variable.type === variableType_AttributeInstance && variable.parents.variableId === firstVar._id);
        return attributeInstance?.name ?? firstVar.name;
    }
    onMultiVarSelected(multiVar) {
        if (multiVar._id === this.state.selectedMultiVarId)
            return;
        const selected = { ...this.getQueryParam('selected', {}) };
        selected[DBDef_MultiVar.dbKey] = multiVar._id;
        this.setQueryParam('selected', selected);
    }
    handleAddVariableClick = () => {
        Dialog_MultiVar_Editor_V2.show({
            diseaseProfileId: this.state.diseaseProfile._id,
        });
    };
    toggleSortingMethod = () => {
        this.setState({ sortMethod: this.state.sortMethod === 'abc' ? 'created' : 'abc' });
        this.forceUpdate();
        this.logInfo(this.state.sortMethod);
    };
    getDVCountMap = () => {
        const mvIds = this.state.multiVars.map(dbObjectToId);
        return ModuleFE_DiseaseValue.cache.all().reduce((map, dv) => {
            if (!mvIds.includes(dv.multiVarId))
                return map;
            if (!map[dv.multiVarId])
                map[dv.multiVarId] = 1;
            else
                map[dv.multiVarId]++;
            return map;
        }, {});
    };
    getSortedMultiVars(state = this.state) {
        switch (state.sortMethod) {
            case 'abc':
                return sortArray(state.multiVars, mv => this.resolveDisplayText(mv));
            case 'created':
                return sortArray(state.multiVars, mv => mv.__created);
            default:
                throw new BadImplementationException(`No implementation for sort type ${state.sortMethod}`);
        }
    }
    //######################### Render #########################
    render() {
        return _jsxs(LL_V_L, { className: 'panel__multivar-list', children: [this.renderHeader(), this.renderList()] });
    }
    renderHeader() {
        const SortRenderer = this.renderSortingButton();
        return _jsx(Panel_Header, { config: { filter: this.state.filter }, onFilterChanged: (filter) => this.setQueryParam('filterMultiVar', filter), qsearchId: 'panel-variable-search', headerClassName: 'panel__multivar-list__header', title: { label: 'Variables', className: 'panel__variables__header__title' }, headerTail: _jsxs(_Fragment, { children: [_jsx(SortRenderer.component, { className: 'tail-icon', onClick: this.toggleSortingMethod }), _jsx("div", { className: 'add-button', onClick: this.handleAddVariableClick, children: "+" })] }) });
    }
    renderSortingButton = () => {
        return this.state.sortMethod === 'abc' ? ICONS.sortABUp : ICONS.sortTimeUp;
    };
    renderList() {
        const dvCountMap = this.getDVCountMap();
        const multiVars = this.getSortedMultiVars();
        return _jsx(LL_V_L, { className: 'multivar-list', children: multiVars.map(mv => this.renderMultiVar(mv, dvCountMap[mv._id] ?? 0)) });
    }
    //######################### Render - MultiVar #########################
    renderMultiVar = (mv, dvCount) => {
        const className = _className('multivar', this.state.selectedMultiVarId === mv._id && 'selected');
        return _jsxs(LL_H_C, { className: className, onClick: () => this.onMultiVarSelected(mv), children: [this.renderChiefComplaintIcon(mv), this.renderMultiVarLabel(mv), _jsxs(LL_H_C, { className: 'multivar-tail', children: [this.renderEditButton(mv), this.renderVariant(mv), this.renderMissingInfo(mv), _jsx("div", { className: 'disease-value-count', children: dvCount }), this.renderVariableToolTip(ModuleFE_Variable.cache.unique(mv.variables[0].variableId), mv)] })] }, mv._id);
    };
    renderMultiVarLabel = (mv) => {
        const toShow = this.resolveDisplayText(mv).split('\.')
            .map((text, i, full) => _jsxs(React.Fragment, { children: [_jsx("span", { className: 'text', children: text }), i < full.length - 1 && _jsx("span", { className: 'dot', children: "." })] }, text));
        return _jsx("div", { className: _className('multivar-name'), children: toShow });
    };
    renderMissingInfo = (mv) => {
        const mvValues = ModuleFE_DiseaseValue.cache.filter(item => item.multiVarId === mv._id);
        const firstVarRowFull = mvValues.every(item => _keys(item.values).includes(mv._firstVarId));
        if (mv.referenceIds.length && mvValues.length && firstVarRowFull)
            return;
        return _jsx(ICONS.missingInfo.component, {});
    };
    renderEditButton = (mv) => {
        return _jsx("div", { className: 'edit-button', onClick: (e) => {
                stopPropagation(e);
                Dialog_MultiVar_Editor_V2.show({
                    diseaseProfileId: this.state.diseaseProfile._id,
                    multiVar: mv
                });
            }, children: _jsx(ICONS.edit.component, {}) });
    };
    renderVariant = (mv) => {
        if (!this.state.diseaseProfile?.variantsGroupId)
            return '';
        const variantGroup = mv.variables[0].tagIds.find(i => i.key === this.state.diseaseProfile.variantsGroupId);
        if (!variantGroup)
            return '';
        return _jsx(LL_H_C, { className: 'variants', children: variantGroup.value.map(id => {
                const label = ModuleFE_Tag.cache.unique(id).label;
                return _jsx("div", { className: 'variant', children: label }, id);
            }) });
    };
    renderVariableToolTip = (variable, mv) => {
        return _jsx("div", { className: 'variable-info-tooltip-icon', ...openContent.tooltip.right('variable-info-tooltip', () => _jsx(Tooltip_VariableInformation, { variable: variable, tagIds: mv.variables[0].tagIds }), { contentHoverDelay: defaultTooltipDelay }), children: "i" });
    };
    renderChiefComplaintIcon = (mv) => {
        if (!this.state.diseaseProfile?.chiefComplaintIds.includes(mv._firstVarId))
            return '';
        return _jsx(ICONS.crown.component, { className: 'chief-complaint-icon' });
    };
}
//# sourceMappingURL=Panel_MultiVarValList.js.map